import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsamcoAuthService, AsamcoUserService } from '@asamco/base';
import { pathJoin, showErrorSwal } from '@asamco/utils';
import { DxLookupComponent, DxLookupModule } from 'devextreme-angular';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApplicationLogin } from '../../interfaces/application-login.interface';
import { Link } from '../../interfaces/link.interface';
import { MarketPlaceItem } from '../../interfaces/market-place-item.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { ApplicationLoginService } from '../../services/application-login.service';
import { LinkEditApplicationLoginModalComponent } from '../link-edit-application-login-modal/link-edit-application-login-modal.component';
import { LinkEditAuthGenericModalComponent } from '../link-edit-setup/link-edit-auth-generic-modal/link-edit-auth-generic-modal.component';
import { LinkService } from '../../services/link.service';
import { DxTemplateModule } from 'devextreme-angular/core';
import { AsLet } from '../../../../../../base/src/lib/as-let/as-let.directive';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';
import { ApplicationParams } from '../../interfaces/application-login-params.interface';
import { ApplicationLoginParamsService } from '../../services/application-login-params.service';
import { ApplicationLoginType } from '../../enums/application-login-type.enum';
import notify from 'devextreme/ui/notify';
import { LinkEditApplicationLoginAuthComponent } from '../link-edit-application-login-auth/link-edit-application-login-auth.component';
import { LinkEditApplicationLoginArticleAdminComponent } from '../link-edit-application-login-article-admin/link-edit-application-login-article-admin.component';
import { LinkEditApplicationLoginHelper } from '../link-edit-application-login-modal/link-edit-application-login-helper';
import { LoginSaveAction } from '../../enums/login-save-action.enum';

@Component({
	selector: 'al-link-edit-login-details',
	templateUrl: './link-edit-login-details.component.html',
	styleUrls: ['./link-edit-login-details.component.scss'],
	standalone: true,
	imports: [
		NgIf,
		AsLet,
		DxLookupModule,
		DxTemplateModule,
		NgClass,
		LinkEditApplicationLoginModalComponent,
		FormsModule,
		ReactiveFormsModule,
		LinkEditAuthGenericModalComponent,
		LinkEditApplicationLoginAuthComponent,
		LinkEditApplicationLoginArticleAdminComponent,
		AsyncPipe,
	],
})
export class LinkEditLoginDetailsComponent implements OnInit, OnDestroy {
	@ViewChild('editAppFieldModal') editAppFields: LinkEditAuthGenericModalComponent;

	@Input()
	formGroup2: FormGroup;

	@Input()
	link: Link;

	@Input()
	linkInfo: MarketPlaceItem;

	@Input()
	fromAppName: string;

	@Input()
	toAppName: string;

	@ViewChild(LinkEditApplicationLoginModalComponent)
	editLoginModal: LinkEditApplicationLoginModalComponent;

	@ViewChild(LinkEditApplicationLoginArticleAdminComponent)
	editLoginArticlesModal: LinkEditApplicationLoginArticleAdminComponent;

	@ViewChild('fromAppLookup')
	fromAppLookup: DxLookupComponent;

	@ViewChild('toAppLookup')
	toAppLookup: DxLookupComponent;

	private destroy$ = new Subject<void>();

	isAdmin: boolean = false;

	public fromApplicationLoginsSubject: BehaviorSubject<ApplicationLogin[]> = new BehaviorSubject<ApplicationLogin[]>(undefined);
	public toApplicationLoginsSubject: BehaviorSubject<ApplicationLogin[]> = new BehaviorSubject<ApplicationLogin[]>(undefined);

	public fromApplicationLogins$: Observable<ApplicationLogin[]> = this.fromApplicationLoginsSubject.asObservable();
	public toApplicationLogins$: Observable<ApplicationLogin[]> = this.toApplicationLoginsSubject.asObservable();

	public selectedFromApplicationLogin: BehaviorSubject<ApplicationLogin> = new BehaviorSubject<ApplicationLogin>(undefined);
	public selectedToApplicationLogin: BehaviorSubject<ApplicationLogin> = new BehaviorSubject<ApplicationLogin>(undefined);

	public selectedFromApplicationLogin$ = this.selectedFromApplicationLogin.asObservable();
	public selectedToApplicationLogin$ = this.selectedToApplicationLogin.asObservable();

	public fromApplicationLoginParamsSubject: Subject<ApplicationParams> = new BehaviorSubject<ApplicationParams>(undefined);
	public toApplicationLoginParamsSubject: Subject<ApplicationParams> = new BehaviorSubject<ApplicationParams>(undefined);

	public fromApplicationLoginParams$: Observable<ApplicationParams> = this.fromApplicationLoginParamsSubject.asObservable();
	public toApplicationLoginParams$: Observable<ApplicationParams> = this.toApplicationLoginParamsSubject.asObservable();

	fromButtonsDisabled$ = new BehaviorSubject<boolean>(true);
	toButtonsDisabled$ = new BehaviorSubject<boolean>(true);

	fromHelper: LinkEditApplicationLoginHelper;
	toHelper: LinkEditApplicationLoginHelper;

	get fromLoginContext() {
		return combineLatest([this.fromApplicationLogins$, this.selectedFromApplicationLogin$, this.fromApplicationLoginParams$]).pipe(
			map(res => {
				// login not selectable for inty users (automatic creation via messagebus) or any applogins without parameters other than name
				const loginSelectable = !this.fromAppName.toLocaleLowerCase().includes('inty') && (res[2]?.oAuth || res[2]?.parameters.some(p => p.fieldName.toLowerCase() !== 'name'));
				return loginSelectable ? {fromApplicationLogins: res[0], data: res[1], fromApplicationLoginParams: res[2]} : null;
			})
		);
	}

	get toLoginContext() {
		return combineLatest([this.toApplicationLogins$, this.selectedToApplicationLogin$, this.toApplicationLoginParams$]).pipe(
			map(res => {
				const loginSelectable = res[2]?.oAuth || res[2]?.parameters.some(p => p.fieldName.toLowerCase() !== 'name');
				return loginSelectable ? {toApplicationLogins: res[0], data: res[1], toApplicationLoginParams: res[2]} : null;
			})
		);
	}

	currentCompanyId: number;

	public loadedLogins: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	formGroup: FormGroup = new FormGroup({});

	constructor(
		private asamcoUserService: AsamcoUserService,
		private selectedCompanyService: SelectedCompanyService,
		private linkService: LinkService,
		private applicationLoginService: ApplicationLoginService,
		private appLoginParamsService: ApplicationLoginParamsService,
		private cdr: ChangeDetectorRef
	) { }

	ngOnInit(): void {
		this.currentCompanyId = this.selectedCompanyService.getSelectedCompanyID();

		this.asamcoUserService.isAdmin().then(result => (this.isAdmin = result));
		this.initForm();

		this.loadedLogins.next(true);
	}

	private initForm() {
		this.linkService.get(this.link.id).subscribe(e => {
			this.formGroup.addControl(
				'from',
				new FormGroup({
					id: new FormControl(null),
					applicationId: new FormControl(this.linkInfo.fromApplicationId),
					freshdeskArticleIds: new FormControl([]),
				})
			);
			this.formGroup.addControl(
				'to',
				new FormGroup({
					id: new FormControl(null),
					applicationId: new FormControl(this.linkInfo.toApplicationId),
					freshdeskArticleIds: new FormControl([]),
				})
			);
			this.link = e;

			this.appLoginParamsService.getParams(this.linkInfo.fromApplicationId).subscribe(ap => {
				this.fromApplicationLoginParamsSubject.next(ap);
				this.fromHelper = this.initializeForm(this.formGroup.get('from'), this.linkInfo.fromApplicationId, ap);

				this.applicationLoginService
				.getApplicationLoginsByApplicationId(this.linkInfo.fromApplicationId, this.currentCompanyId)
				.subscribe({next: resp => {
					this.fromApplicationLoginsSubject.next(resp);
					let foundFromitem = resp.filter((e: ApplicationLogin) => e.id === this.link.fromApplicationLoginId)[0];
					this.selectedFromApplicationLogin.next(foundFromitem);
					this.fromHelper.setLogin(foundFromitem);
					this.fromButtonsDisabled$.next(false);
				}, error: showErrorSwal});
			});

			this.appLoginParamsService.getParams(this.linkInfo.toApplicationId).subscribe(ap => {
				this.toApplicationLoginParamsSubject.next(ap);
				this.toHelper = this.initializeForm(this.formGroup.get('to'), this.linkInfo.toApplicationId, ap);

				this.applicationLoginService
				.getApplicationLoginsByApplicationId(this.linkInfo.toApplicationId, this.currentCompanyId)
				.subscribe({next: resp => {
					this.toApplicationLoginsSubject.next(resp);
					let foundToitem = resp.filter((e: ApplicationLogin) => e.id === this.link.toApplicationLoginId)[0];
					this.selectedToApplicationLogin.next(foundToitem);
					this.toHelper.setLogin(foundToitem);
					this.toButtonsDisabled$.next(false);
				}, error: showErrorSwal});
			});
		});
	}

	public valueChanged(event, part: string) {
		if(!event.event) return;

		switch (part) {
			case 'from':
				this.selectedFromApplicationLogin.next(event.value);
				this.fromHelper.setLogin(event.value);
				break;
			case 'to':
				this.selectedToApplicationLogin.next(event.value);
				this.toHelper.setLogin(event.value);
				break;
		}

		const buttonsSubject = part === 'from' ? this.fromButtonsDisabled$ : this.toButtonsDisabled$;
		buttonsSubject.next(true);

		this.applicationLoginService.updateApploginId(this.link.id, part, event.value.id).subscribe({
			next: result => {
				if (result.success) {
					notify({
						message: 'Successfully saved login selection',
						type: 'success',
						displayTime: 1000,
					});
				}
				buttonsSubject.next(false);
			},
			error: error => {
				showErrorSwal(error);
				buttonsSubject.next(false);
			}
		});
	}

	initializeForm(formGroup, applicationId: number, appParams) {
		const helper = new LinkEditApplicationLoginHelper(formGroup, applicationId, appParams, this.cdr);
		helper.initializeParameterFormInitial();
		helper.checkFormGroupForLeadingOrTrailingSpace();
		return helper;
	}

	editApplicationLoginBtn(formGroup, loginType: ApplicationLoginType, applicationId: number, initLogin: ApplicationLogin, appSide: string, appParams: ApplicationParams) {
		if(appSide === 'from') {
			this.fromHelper.setLogin(initLogin);
		} else {
			this.toHelper.setLogin(initLogin);
		}

		this.editLoginModal.show(formGroup, loginType, applicationId, this.linkInfo.type, initLogin, appSide, this.link.id, appParams);
	}

	deleteApplicationLoginBtn(event, item: ApplicationLogin, appSide: string) {
		if (event?.stopPropagation) {
			event.stopPropagation();
		}

		//Check if current login is active on any other links
		this.applicationLoginService.checkIfLoginIsUsed(item.id).subscribe((loginIsBeingUsed: Boolean) => {
			let text = 'Are you sure you want to delete the following login: ' + item.name;
			if (loginIsBeingUsed) {
				text = `The application login you are about to delete (${item.name}) has been assigned to one or more of your integration tiles.`;
			}

			Swal.fire({
				icon: 'warning',
				title: 'Are you sure?',
				text: text,
				showCancelButton: true,
				confirmButtonColor: '#dd6b55',
				cancelButtonColor: '#999',
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel',
			}).then((result) => {
				if (result.value && !result.dismiss) {
					this.applicationLoginService.deleteApplicationLogin(item.id).subscribe({
						next: () => {
							Swal.fire({
								icon: 'success',
								text: 'Successfully deleted login entry',
							});

							if(appSide === 'from') {
								this.fromApplicationLoginsSubject.next(this.fromApplicationLoginsSubject.value.filter(al => al.id !== item.id));
								this.selectedFromApplicationLogin.next(undefined);
								this.fromHelper.setLogin(undefined);
							} else {
								this.toApplicationLoginsSubject.next(this.toApplicationLoginsSubject.value.filter(al => al.id !== item.id));
								this.selectedToApplicationLogin.next(undefined);
								this.toHelper.setLogin(undefined);
							}

						},
						error: showErrorSwal
					});
				}
			});
		});
	}

	openAppLoginManagementModal() {
		this.editAppFields.showModal();
	}

	openApplicationLoginArticleAdmin(login: ApplicationLogin, appSide: string) {
		if (!login?.id) return;

		const applicationName = appSide === 'from' ? this.fromAppName : this.toAppName;
		this.editLoginArticlesModal.open(login, appSide, this.link.id, applicationName);
	}

	onLoginSaved($event) {
		if($event.appSide === 'from') {
			if($event.action === LoginSaveAction.Created) {
				this.fromApplicationLoginsSubject.next(this.fromApplicationLoginsSubject.value.concat([$event.appLogin]));
				this.selectedFromApplicationLogin.next($event.appLogin);
			} else if($event.action === LoginSaveAction.Saved) {
				Object.assign(this.selectedFromApplicationLogin.value, $event.appLogin);
				this.fromApplicationLoginsSubject.next(this.fromApplicationLoginsSubject.value);
				this.selectedFromApplicationLogin.next(this.selectedFromApplicationLogin.value);
			}
			this.fromHelper.setLogin(this.selectedFromApplicationLogin.value);
		} else {
			if($event.action === LoginSaveAction.Created) {
				this.toApplicationLoginsSubject.next(this.toApplicationLoginsSubject.value.concat([$event.appLogin]));
				this.selectedToApplicationLogin.next($event.appLogin);
			} else if($event.action === LoginSaveAction.Saved) {
				Object.assign(this.selectedToApplicationLogin.value, $event.appLogin);
				this.toApplicationLoginsSubject.next(this.toApplicationLoginsSubject.value);
				this.selectedToApplicationLogin.next(this.selectedToApplicationLogin.value);
			}
			this.toHelper.setLogin(this.selectedToApplicationLogin.value);
		}
	}

	onLoginArticlesSaved($event: {appLogin: ApplicationLogin, appSide: string}) {
		if($event.appSide === 'from') {
			Object.assign(this.selectedFromApplicationLogin.value, $event.appLogin);
			this.fromApplicationLoginsSubject.next(this.fromApplicationLoginsSubject.value);
			this.selectedFromApplicationLogin.next(this.selectedFromApplicationLogin.value);
			this.fromHelper.setLogin(this.selectedFromApplicationLogin.value);
		} else {
			Object.assign(this.selectedToApplicationLogin.value, $event.appLogin);
			this.toApplicationLoginsSubject.next(this.toApplicationLoginsSubject.value);
			this.selectedToApplicationLogin.next(this.selectedToApplicationLogin.value);
			this.toHelper.setLogin(this.selectedToApplicationLogin.value);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
